import { useState, useCallback, useEffect } from 'react';
import type { PoolScenario } from '../types';

const MAX_HISTORY = 50; // Limit history to 50 states
const HISTORY_STORAGE_KEY = 'zfs-calculator-history';

interface ScenarioHistoryState {
  past: PoolScenario[][];
  present: PoolScenario[];
  future: PoolScenario[][];
}

/**
 * Custom hook for managing scenario history with undo/redo functionality.
 * Tracks up to 50 states and persists to localStorage.
 * Provides undo/redo capabilities for scenario management.
 *
 * @param initialScenarios - Initial scenarios to load (defaults to empty array)
 * @returns History state and control functions
 */
export function useScenarioHistory(initialScenarios: PoolScenario[] = []) {
  const [history, setHistory] = useState<ScenarioHistoryState>({
    past: [],
    present: initialScenarios,
    future: [],
  });

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored) {
        const savedHistory = JSON.parse(stored) as ScenarioHistoryState;
        setHistory(savedHistory);
      }
    } catch (e) {
      console.error('Failed to load history from localStorage:', e);
    }
  }, []);

  // Save to localStorage whenever history changes
  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
    } catch (e) {
      if (e instanceof Error && e.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Unable to save history.');
      } else {
        console.error('Failed to save history to localStorage:', e);
      }
    }
  }, [history]);

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  /**
   * Save a new state to history
   * Clears the future stack (standard undo/redo behavior)
   */
  const push = useCallback((newScenarios: PoolScenario[]) => {
    setHistory(prev => {
      // Don't save if state hasn't changed
      if (JSON.stringify(prev.present) === JSON.stringify(newScenarios)) {
        return prev;
      }

      const newPast = [...prev.past, prev.present];

      // Limit history size
      if (newPast.length > MAX_HISTORY) {
        newPast.shift(); // Remove oldest state
      }

      return {
        past: newPast,
        present: newScenarios,
        future: [], // Clear future on new action
      };
    });
  }, []);

  /**
   * Undo to previous state
   */
  const undo = useCallback(() => {
    if (!canUndo) return;

    setHistory(prev => {
      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, prev.past.length - 1);
      const newFuture = [prev.present, ...prev.future];

      return {
        past: newPast,
        present: previous,
        future: newFuture,
      };
    });
  }, [canUndo]);

  /**
   * Redo to next state
   */
  const redo = useCallback(() => {
    if (!canRedo) return;

    setHistory(prev => {
      const next = prev.future[0];
      const newPast = [...prev.past, prev.present];
      const newFuture = prev.future.slice(1);

      return {
        past: newPast,
        present: next,
        future: newFuture,
      };
    });
  }, [canRedo]);

  /**
   * Reset history to a new state
   * Clears all past and future states
   */
  const reset = useCallback((scenarios: PoolScenario[]) => {
    setHistory({
      past: [],
      present: scenarios,
      future: [],
    });
  }, []);

  /**
   * Clear all history
   */
  const clear = useCallback(() => {
    setHistory({
      past: [],
      present: [],
      future: [],
    });
    try {
      localStorage.removeItem(HISTORY_STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear history from localStorage:', e);
    }
  }, []);

  return {
    scenarios: history.present,
    setScenarios: push,
    undo,
    redo,
    canUndo,
    canRedo,
    reset,
    clear,
    historySize: history.past.length,
  };
}
